import React, { useCallback, useEffect, useMemo, useState } from "react";
import studies from "../../Data/studies.json";
import { findStudyCategory } from "../../utils/studies";
import { getDbSettings } from "../../utils/dbConfig";
import { fetchAllSurveyData, fetchDemocracyCheckupData, isRequestAborted } from "../../utils/api";
import ShowStatsSection from "../Common/ShowStatsSection";
import ChartCanvas from "./ChartCanvas";
import ChartControls from "./ChartControls";
import ChartStatisticsPanel, {
  ChartStatisticsComparisonByCategoryPanel,
} from "./ChartStatisticsPanel";
import { useChartData } from "./hooks/useChartData";
import { useChartExport } from "./hooks/useChartExport";
import { useChartFilters } from "./hooks/useChartFilters";
import { buildChartGraphData, useChartGraphData } from "./hooks/useChartGraphData";
import { useChartStatistics } from "./hooks/useChartStatistics";
import { computeStatistics } from "../Statistics/utils";
import {
  buildQuestionsIndexForPrefix,
  isExceptionVar,
  maybeBuildGroupedFilters,
  resolveVariableIdForYear,
  yearToPrefix,
} from "./utils/chartHelpers";

const normalizeMappingKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const mapSelectedCodesToYear = ({ selectedCodes = [], sourceMapping = {}, targetMapping = {} }) => {
  if (!selectedCodes.length) return [];

  const targetEntries = Object.entries(targetMapping || {});
  const targetByDisplay = new Map(
    targetEntries.map(([rawCode, entry]) => [
      normalizeMappingKey(entry?.Display ?? entry?.display ?? rawCode),
      String(rawCode),
    ])
  );

  const mappedCodes = new Set();
  selectedCodes.forEach((code) => {
    const normalizedRaw = String(code);

    if (Object.prototype.hasOwnProperty.call(targetMapping || {}, normalizedRaw)) {
      mappedCodes.add(normalizedRaw);
      return;
    }

    const sourceEntry = sourceMapping?.[normalizedRaw];
    const displayKey = normalizeMappingKey(sourceEntry?.Display ?? sourceEntry?.display ?? code);
    const mappedRaw = targetByDisplay.get(displayKey);
    if (mappedRaw) mappedCodes.add(mappedRaw);
  });

  return Array.from(mappedCodes);
};

const withYearGroupLabel = (rows = [], yearLabel = "") =>
  rows.map((row) => ({
    ...row,
    group: yearLabel,
    groupRaw: yearLabel,
  }));

const sortMappingEntries = (mapping = {}) =>
  Object.entries(mapping).sort(([leftKey], [rightKey]) => {
    const leftNum = Number(leftKey);
    const rightNum = Number(rightKey);
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum - rightNum;
    return String(leftKey).localeCompare(String(rightKey));
  });

const normalizeEndpointLabel = (value, fallback) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.;,:-]+$/, "");
  return normalized || fallback;
};

const extractScaleEndpoints = (question) => {
  const text = String(question || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;

  const explicitMatch = text.match(
    /0\s*(?:means|=)\s*([^.;,]+?)\s*(?:and|,)\s*100\s*(?:means|=)\s*([^.;,]+)/i
  );
  if (explicitMatch) {
    return {
      low: normalizeEndpointLabel(explicitMatch[1], "lowest possible rating"),
      high: normalizeEndpointLabel(explicitMatch[2], "highest possible rating"),
    };
  }

  return null;
};

const formatSourceLine = (studyNames = []) => {
  const uniqueNames = Array.from(
    new Set((studyNames || []).map((name) => String(name || "").trim()).filter(Boolean))
  );

  if (!uniqueNames.length) return "";
  if (uniqueNames.length === 1) return `Source: ${uniqueNames[0]}`;
  if (uniqueNames.length === 2) return `Source: ${uniqueNames[0]} vs ${uniqueNames[1]}`;
  return `Source: ${uniqueNames.join("; ")}`;
};

const normalizeRowValue = (value) => String(value ?? "").trim();

const computeStatsFromGraphRows = ({
  graphRows = [],
  depType = "cat",
  hasComparisonCategory = false,
}) => {
  const points = (graphRows || [])
    .map((row) => {
      const numericValue =
        String(depType || "").toLowerCase() === "num"
          ? Number(row?.y)
          : Number(normalizeRowValue(row?.depRaw));
      const weight = Number(row?.weight);
      const groupLabel = normalizeRowValue(row?.group);
      return {
        value: numericValue,
        weight: Number.isFinite(weight) && weight > 0 ? weight : 0,
        groupLabel,
      };
    })
    .filter((point) => Number.isFinite(point.value) && point.value !== -99 && point.weight > 0);

  if (!points.length) {
    return { nationalStats: null, indepStats: [] };
  }

  const nationalStats = computeStatistics(points, "value", {
    weightAccessor: (point) => point.weight,
  });

  if (!hasComparisonCategory) {
    return { nationalStats, indepStats: [] };
  }

  const grouped = new Map();
  points.forEach((point) => {
    if (!point.groupLabel) return;
    const existing = grouped.get(point.groupLabel) || [];
    existing.push(point);
    grouped.set(point.groupLabel, existing);
  });

  const indepStats = Array.from(grouped.entries())
    .map(([label, groupPoints]) => ({
      label,
      ...computeStatistics(groupPoints, "value", {
        weightAccessor: (point) => point.weight,
      }),
    }))
    .filter((entry) => entry.sampleSize > 0)
    .sort((left, right) => String(left.label).localeCompare(String(right.label)));

  return { nationalStats, indepStats };
};

const ChartForm = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedIndependent, setSelectedIndependent] = useState("");
  const [selectedDependent, setSelectedDependent] = useState("");
  const [chartType, setChartType] = useState("");
  const [graphTitle, setGraphTitle] = useState("");
  const [useWeightedData, setUseWeightedData] = useState(false);
  const [barYAxisMetric, setBarYAxisMetric] = useState("frequency");

  const [selectedComparisonStudyNames, setSelectedComparisonStudyNames] = useState([]);
  const [isYearComparisonActive, setIsYearComparisonActive] = useState(false);
  const [yearComparisonError, setYearComparisonError] = useState(null);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [comparisonDataByStudyName, setComparisonDataByStudyName] = useState({});
  const [isComparisonDataLoading, setIsComparisonDataLoading] = useState(false);

  const selectedStudyCategory = useMemo(
    () => findStudyCategory(studies, selectedFile),
    [selectedFile]
  );

  const comparisonStudyOptions = useMemo(() => {
    if (!selectedFile || !selectedStudyCategory) return [];

    return (selectedStudyCategory?.files || [])
      .filter((file) => file?.name && file.name !== selectedFile.name)
      .sort((left, right) => Number(right?.year || 0) - Number(left?.year || 0));
  }, [selectedFile, selectedStudyCategory]);

  const selectedComparisonStudies = useMemo(
    () =>
      comparisonStudyOptions.filter((option) =>
        selectedComparisonStudyNames.includes(String(option?.name || ""))
      ),
    [comparisonStudyOptions, selectedComparisonStudyNames]
  );
  const missingComparisonStudies = useMemo(
    () =>
      selectedComparisonStudies.filter((study) => {
        const studyName = String(study?.name || "");
        if (!studyName) return false;
        return !Object.prototype.hasOwnProperty.call(comparisonDataByStudyName, studyName);
      }),
    [comparisonDataByStudyName, selectedComparisonStudies]
  );

  const comparisonQuestionsIndexByStudyName = useMemo(
    () =>
      Object.fromEntries(
        selectedComparisonStudies.map((study) => {
          const prefix = yearToPrefix(study?.year);
          return [study.name, prefix ? buildQuestionsIndexForPrefix(prefix) : {}];
        })
      ),
    [selectedComparisonStudies]
  );

  const { sqlData, isLoading, dataError, questionsIndex, indepGroups, depGroups } =
    useChartData(selectedFile);

  useEffect(() => {
    setSelectedIndependent("");
    setSelectedDependent("");
    setSelectedComparisonStudyNames([]);
    setIsYearComparisonActive(false);
    setYearComparisonError(null);
    setComparisonResults([]);
    setComparisonDataByStudyName({});
  }, [selectedFile]);

  const flatIndepOptions = useMemo(
    () => indepGroups.flatMap((group) => group.options || []),
    [indepGroups]
  );
  const flatDepOptions = useMemo(
    () => depGroups.flatMap((group) => group.options || []),
    [depGroups]
  );

  const selectedIndepMeta = useMemo(
    () => flatIndepOptions.find((option) => option.id === selectedIndependent) || null,
    [flatIndepOptions, selectedIndependent]
  );
  const selectedDepMeta = useMemo(
    () => flatDepOptions.find((option) => option.id === selectedDependent) || null,
    [flatDepOptions, selectedDependent]
  );

  const weightColumn = useMemo(() => {
    if (!selectedFile?.year) return null;
    const yearSuffix = String(selectedFile.year).slice(-2).padStart(2, "0");
    return `dc${yearSuffix}_quota_weight`;
  }, [selectedFile]);

  const {
    filters,
    selectedFilters,
    isModalOpen,
    toggleModal,
    handleFilterChange,
    dependentAnswerMapping,
    dependentFilters,
    selectedDependentFilters,
    isDepModalOpen,
    toggleDepModal,
    handleDependentFilterChange,
    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
    applyActiveFilters,
  } = useChartFilters({
    selectedIndependent,
    selectedDependent,
    questionsIndex,
  });

  const outcomeResponseEntries = useMemo(
    () =>
      sortMappingEntries(dependentAnswerMapping)
        .map(([, value]) => String(value?.Display || "").trim())
        .filter(Boolean),
    [dependentAnswerMapping]
  );
  const outcomeQuestionText = useMemo(
    () => (selectedDependent ? questionsIndex[selectedDependent]?.mainQuestion || "" : ""),
    [questionsIndex, selectedDependent]
  );
  const numericOutcomeGuide = useMemo(() => {
    if (String(selectedDepMeta?.type || "").toLowerCase() !== "num") return null;

    const numericValues = outcomeResponseEntries
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isFinite(value));
    if (!numericValues.length) return null;

    const uniqueValues = new Set(numericValues);
    const minValue = Math.min(...numericValues);
    const maxValue = Math.max(...numericValues);
    const looksLikeHundredScale = minValue === 0 && maxValue === 100 && uniqueValues.size >= 50;
    if (!looksLikeHundredScale) return null;

    const extracted = extractScaleEndpoints(outcomeQuestionText);
    return {
      scaleLabel: "0-100 scale",
      lowLabel: extracted?.low || "lowest possible rating",
      highLabel: extracted?.high || "highest possible rating",
    };
  }, [selectedDepMeta?.type, outcomeQuestionText, outcomeResponseEntries]);

  const { graphData, graphError, generateGraph, resetGraph } = useChartGraphData({
    sqlData,
    selectedIndependent,
    selectedDependent,
    useWeightedData,
    weightColumn,
    selectedIndepMeta,
    selectedDepMeta,
    questionsIndex,
    dependentAnswerMapping,
    applyActiveFilters,
    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
  });

  const sourceIndependentAnswerMapping = useMemo(
    () => (selectedIndependent ? questionsIndex[selectedIndependent]?.answersMapping || {} : {}),
    [questionsIndex, selectedIndependent]
  );
  const applyComparisonFiltersForStudy = useCallback(
    ({
      rows = [],
      comparisonSelectedIndependent = "",
      comparisonSelectedDependent = "",
      comparisonIndependentAnswerMapping = {},
      comparisonDependentAnswerMapping = {},
      comparisonIndepIsGrouped = false,
      comparisonIndepGroupMembers = {},
    }) => {
      let filteredRows = [...rows];
      const hasIndependentSelection = Boolean(selectedIndependent && comparisonSelectedIndependent);

      if (hasIndependentSelection && selectedFilters.length > 0) {
        if (indepIsGrouped && comparisonIndepIsGrouped) {
          const memberSets = selectedFilters.map(
            (groupId) => new Set((comparisonIndepGroupMembers[groupId] || []).map(String))
          );

          filteredRows = filteredRows.filter((row) => {
            const raw = String(row[comparisonSelectedIndependent]);
            return memberSets.some((set) => set.has(raw));
          });
        } else {
          const mappedIndependentFilters = mapSelectedCodesToYear({
            selectedCodes: selectedFilters,
            sourceMapping: sourceIndependentAnswerMapping,
            targetMapping: comparisonIndependentAnswerMapping,
          });

          if (!mappedIndependentFilters.length) {
            filteredRows = [];
          } else {
            const rawSet = new Set(mappedIndependentFilters.map(String));
            filteredRows = filteredRows.filter((row) =>
              rawSet.has(String(row[comparisonSelectedIndependent]))
            );
          }
        }
      }

      if (comparisonSelectedDependent && selectedDependentFilters.length > 0) {
        const mappedDependentFilters = mapSelectedCodesToYear({
          selectedCodes: selectedDependentFilters,
          sourceMapping: dependentAnswerMapping,
          targetMapping: comparisonDependentAnswerMapping,
        });

        if (!mappedDependentFilters.length) {
          filteredRows = [];
        } else {
          const dependentSet = new Set(mappedDependentFilters.map(String));
          filteredRows = filteredRows.filter((row) =>
            dependentSet.has(String(row[comparisonSelectedDependent]))
          );
        }
      }

      const filterIndependentNa =
        hasIndependentSelection && !isExceptionVar(comparisonSelectedIndependent);
      const filterDependentNa =
        Boolean(comparisonSelectedDependent) && !isExceptionVar(comparisonSelectedDependent);

      if (filterIndependentNa || filterDependentNa) {
        filteredRows = filteredRows.filter((row) => {
          const independentValue = hasIndependentSelection
            ? row[comparisonSelectedIndependent]
            : null;
          const dependentValue = comparisonSelectedDependent
            ? row[comparisonSelectedDependent]
            : null;

          const independentInvalid =
            filterIndependentNa &&
            (String(independentValue) === "-99" || Number(independentValue) === -99);
          const dependentInvalid =
            filterDependentNa &&
            (String(dependentValue) === "-99" || Number(dependentValue) === -99);

          return !(independentInvalid || dependentInvalid);
        });
      }

      return filteredRows;
    },
    [
      dependentAnswerMapping,
      indepIsGrouped,
      selectedDependentFilters,
      selectedFilters,
      selectedIndependent,
      sourceIndependentAnswerMapping,
    ]
  );

  useEffect(() => {
    if (!selectedComparisonStudies.length) {
      setIsComparisonDataLoading(false);
      return;
    }
    if (!missingComparisonStudies.length) {
      setIsComparisonDataLoading(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    setIsComparisonDataLoading(true);

    const loadComparisonData = async () => {
      const entries = await Promise.all(
        missingComparisonStudies.map(async (study) => {
          const studyName = String(study?.name || "");
          try {
            const studyCategory = findStudyCategory(studies, study) || selectedStudyCategory;
            const isDemocracyCheckup = studyCategory?.datasetKey === "democracy_checkup";

            let rows = [];
            if (isDemocracyCheckup) {
              rows = await fetchDemocracyCheckupData(study.year, {
                signal: controller.signal,
              });
            } else {
              const dbSettings = getDbSettings(studyCategory?.datasetKey, {
                year: study?.year,
                provinceKey: study?.provinceKey,
              });
              rows = await fetchAllSurveyData(dbSettings?.dbName, { signal: controller.signal });
            }

            return {
              name: studyName,
              rows: Array.isArray(rows) ? rows : [],
              error: null,
            };
          } catch (error) {
            if (isRequestAborted(error)) throw error;
            return {
              name: studyName,
              rows: [],
              error: "Could not load study data.",
            };
          }
        })
      );

      if (!isMounted) return;
      const loadedByStudyName = Object.fromEntries(
        entries.map((entry) => [entry.name, { rows: entry.rows, error: entry.error }])
      );
      setComparisonDataByStudyName((current) => ({
        ...current,
        ...loadedByStudyName,
      }));
      setIsComparisonDataLoading(false);
    };

    loadComparisonData().catch((error) => {
      if (!isMounted || isRequestAborted(error)) return;
      setIsComparisonDataLoading(false);
    });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [missingComparisonStudies, selectedComparisonStudies.length, selectedStudyCategory]);

  useEffect(() => {
    resetGraph();
  }, [
    selectedDependent,
    selectedIndependent,
    selectedFilters,
    selectedDependentFilters,
    graphTitle,
    useWeightedData,
    chartType,
    resetGraph,
  ]);

  useEffect(() => {
    setComparisonResults([]);
    setIsYearComparisonActive(false);
    setYearComparisonError(null);
  }, [
    selectedDependent,
    selectedIndependent,
    selectedFilters,
    selectedDependentFilters,
    graphTitle,
    useWeightedData,
    chartType,
    selectedComparisonStudyNames,
  ]);

  const {
    showStats,
    setShowStats,
    nationalStats,
    indepStats,
    selectedIndepBucket,
    setSelectedIndepBucket,
    sortBy,
    setSortBy,
    detectNumericScale,
  } = useChartStatistics({
    sqlData,
    selectedIndependent,
    selectedDependent,
    useWeightedData,
    weightColumn,
    dependentAnswerMapping,
    questionsIndex,
    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
    applyActiveFilters,
  });
  const baseYearLabel = selectedFile?.year ? String(selectedFile.year) : "Selected year";
  const hasComparisonCategory = Boolean(selectedIndependent);

  const comparisonDataError = useMemo(() => {
    const firstFailedStudy = selectedComparisonStudies.find(
      (study) => comparisonDataByStudyName[study?.name]?.error
    );
    if (!firstFailedStudy) return null;
    return `${firstFailedStudy.year}: ${comparisonDataByStudyName[firstFailedStudy.name].error}`;
  }, [comparisonDataByStudyName, selectedComparisonStudies]);

  const renderableComparisonResults = useMemo(
    () => comparisonResults.filter((result) => (result?.graphData || []).length > 0),
    [comparisonResults]
  );
  const activeComparisonYearLabels = useMemo(
    () => renderableComparisonResults.map((result) => result.yearLabel),
    [renderableComparisonResults]
  );
  const hasRenderableYearComparisonData =
    isYearComparisonActive && renderableComparisonResults.length > 0;

  const shouldOverlayYearComparison =
    hasRenderableYearComparisonData &&
    !hasComparisonCategory &&
    (chartType === "grouped_bar" || chartType === "density");
  const shouldShowComparisonBelow = hasRenderableYearComparisonData && !shouldOverlayYearComparison;

  const yearComparisonSeries = useMemo(() => {
    if (!shouldOverlayYearComparison) return [];
    return [
      { yearLabel: baseYearLabel, graphData },
      ...renderableComparisonResults.map((result) => ({
        yearLabel: result.yearLabel,
        graphData: result.graphData,
      })),
    ];
  }, [baseYearLabel, graphData, renderableComparisonResults, shouldOverlayYearComparison]);

  const primarySourceLine = useMemo(() => {
    if (shouldOverlayYearComparison) {
      return formatSourceLine([
        selectedFile?.name,
        ...renderableComparisonResults.map((r) => r.studyName),
      ]);
    }
    return formatSourceLine([selectedFile?.name]);
  }, [renderableComparisonResults, selectedFile?.name, shouldOverlayYearComparison]);

  const rendererGraphData = useMemo(() => {
    if (chartType !== "density" || !shouldOverlayYearComparison) return graphData;
    return yearComparisonSeries.flatMap((seriesItem) =>
      withYearGroupLabel(seriesItem.graphData, seriesItem.yearLabel)
    );
  }, [chartType, graphData, shouldOverlayYearComparison, yearComparisonSeries]);

  const rendererIndepMeta = useMemo(() => {
    if (chartType === "density" && shouldOverlayYearComparison && !hasComparisonCategory) {
      return {
        id: "year_overlay",
        type: "cat",
        question: "Year",
        label: "Year",
      };
    }

    return selectedIndepMeta;
  }, [chartType, hasComparisonCategory, selectedIndepMeta, shouldOverlayYearComparison]);

  const comparisonStatsByYear = useMemo(
    () =>
      renderableComparisonResults.map((result) => ({
        ...result,
        ...computeStatsFromGraphRows({
          graphRows: result.graphData,
          depType: selectedDepMeta?.type || "cat",
          hasComparisonCategory,
        }),
      })),
    [hasComparisonCategory, renderableComparisonResults, selectedDepMeta?.type]
  );

  const { exportToPDF, isExporting, exportError } = useChartExport({
    graphData: rendererGraphData,
    dependentAnswerMapping,
    comparisonSeries: renderableComparisonResults.map((result) => ({
      studyName: result.studyName,
      yearLabel: result.yearLabel,
      graphData: result.graphData,
      dependentAnswerMapping: result.dependentAnswerMapping,
    })),
    isYearComparisonActive: hasRenderableYearComparisonData,
    baseYearLabel,
    filters,
    selectedFilters,
    dependentFilters,
    selectedDependentFilters,
    selectedIndependent,
    selectedDependent,
    selectedFile,
    selectedIndepMeta,
    selectedDepMeta,
  });

  const isFormComplete = selectedFile && selectedDependent && chartType && graphTitle;
  const canCompareBetweenYears =
    Boolean(isFormComplete) && graphData.length > 0 && comparisonStudyOptions.length > 0;

  const effectiveYearComparisonError =
    yearComparisonError || (isYearComparisonActive ? comparisonDataError : null) || null;

  const handleToggleStats = useCallback(() => {
    setShowStats((value) => !value);
  }, [setShowStats]);

  const handleCompareBetweenYears = useCallback(() => {
    if (!graphData.length) {
      setIsYearComparisonActive(false);
      setYearComparisonError("Generate the main chart before comparing years.");
      setComparisonResults([]);
      return;
    }

    if (!selectedComparisonStudies.length) {
      setIsYearComparisonActive(false);
      setYearComparisonError("Select at least one year to compare.");
      setComparisonResults([]);
      return;
    }

    if (isComparisonDataLoading) {
      setIsYearComparisonActive(false);
      setYearComparisonError("Comparison years are still loading. Please try again.");
      return;
    }

    const validationMessages = [];
    const nextResults = [];

    selectedComparisonStudies.forEach((study) => {
      const studyName = String(study?.name || "");
      const studyYearLabel = String(study?.year || "Comparison year");
      const studyDataEntry = comparisonDataByStudyName[studyName] || {};
      const studyRows = Array.isArray(studyDataEntry.rows) ? studyDataEntry.rows : [];

      if (studyDataEntry.error) {
        validationMessages.push(`${studyYearLabel}: ${studyDataEntry.error}`);
        return;
      }

      const comparisonQuestionsIndex = comparisonQuestionsIndexByStudyName[studyName] || {};
      const comparisonSelectedIndependent =
        selectedIndependent && study?.year
          ? resolveVariableIdForYear(selectedIndependent, study.year)
          : "";
      const comparisonSelectedDependent =
        selectedDependent && study?.year
          ? resolveVariableIdForYear(selectedDependent, study.year)
          : "";

      if (!comparisonSelectedDependent) {
        validationMessages.push(
          `${studyYearLabel}: Selected outcome is unavailable for the comparison year.`
        );
        return;
      }

      if (selectedIndependent && !comparisonSelectedIndependent) {
        validationMessages.push(
          `${studyYearLabel}: Selected comparison category is unavailable for the comparison year.`
        );
        return;
      }

      const comparisonIndependentMeta = comparisonSelectedIndependent
        ? comparisonQuestionsIndex[comparisonSelectedIndependent] || null
        : null;
      const comparisonDependentMeta = comparisonSelectedDependent
        ? comparisonQuestionsIndex[comparisonSelectedDependent] || null
        : null;
      const comparisonDependentAnswerMapping = comparisonDependentMeta?.answersMapping || {};

      const groupedComparisonIndependent = maybeBuildGroupedFilters({
        mainQuestion: comparisonIndependentMeta?.mainQuestion,
        answersMapping: comparisonIndependentMeta?.answersMapping,
      });
      const comparisonIndepIsGrouped = Boolean(groupedComparisonIndependent?.grouped);
      const comparisonIndepGroupMembers = groupedComparisonIndependent?.groupMembers || {};
      const comparisonIndepGroupDisplay = groupedComparisonIndependent?.displayByGroupId || {};

      const comparisonWeightColumn = study?.year
        ? `dc${String(study.year).slice(-2).padStart(2, "0")}_quota_weight`
        : null;
      const getComparisonRowWeight = (row) => {
        if (!useWeightedData || !comparisonWeightColumn) return 1;
        const parsed = Number(row?.[comparisonWeightColumn]);
        if (!Number.isFinite(parsed)) return 1;
        return Math.max(0, parsed);
      };

      const { graphData: comparisonGraphRows, graphError: comparisonGraphError } =
        buildChartGraphData({
          sqlData: studyRows,
          selectedIndependent: comparisonSelectedIndependent,
          selectedDependent: comparisonSelectedDependent,
          selectedIndepMeta,
          selectedDepMeta,
          questionsIndex: comparisonQuestionsIndex,
          dependentAnswerMapping: comparisonDependentAnswerMapping,
          applyActiveFilters: (rows) =>
            applyComparisonFiltersForStudy({
              rows,
              comparisonSelectedIndependent,
              comparisonSelectedDependent,
              comparisonIndependentAnswerMapping: comparisonIndependentMeta?.answersMapping || {},
              comparisonDependentAnswerMapping,
              comparisonIndepIsGrouped,
              comparisonIndepGroupMembers,
            }),
          indepIsGrouped: comparisonIndepIsGrouped,
          indepGroupMembers: comparisonIndepGroupMembers,
          indepGroupDisplay: comparisonIndepGroupDisplay,
          getRowWeight: getComparisonRowWeight,
        });

      if (!comparisonGraphRows.length && comparisonGraphError) {
        validationMessages.push(`${studyYearLabel}: ${comparisonGraphError}`);
        return;
      }

      nextResults.push({
        studyName,
        yearLabel: studyYearLabel,
        graphData: comparisonGraphRows,
        graphError: comparisonGraphError,
        dependentAnswerMapping: comparisonDependentAnswerMapping,
        indepIsGrouped: comparisonIndepIsGrouped,
      });
    });

    if (!nextResults.length) {
      setComparisonResults([]);
      setIsYearComparisonActive(false);
      setYearComparisonError(validationMessages[0] || "No comparison data is available.");
      return;
    }

    setComparisonResults(nextResults);
    setIsYearComparisonActive(true);
    setYearComparisonError(validationMessages.length ? validationMessages.join(" ") : null);
  }, [
    applyComparisonFiltersForStudy,
    comparisonDataByStudyName,
    comparisonQuestionsIndexByStudyName,
    graphData.length,
    isComparisonDataLoading,
    selectedComparisonStudies,
    selectedDependent,
    selectedIndepMeta,
    selectedDepMeta,
    selectedIndependent,
    useWeightedData,
  ]);

  return (
    <div className="px-4 py-3">
      <div className="flex flex-col gap-8">
        <ChartControls
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          selectedIndependent={selectedIndependent}
          setSelectedIndependent={setSelectedIndependent}
          indepGroups={indepGroups}
          filters={filters}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
          selectedDependent={selectedDependent}
          setSelectedDependent={setSelectedDependent}
          depGroups={depGroups}
          dependentFilters={dependentFilters}
          isDepModalOpen={isDepModalOpen}
          toggleDepModal={toggleDepModal}
          selectedDependentFilters={selectedDependentFilters}
          handleDependentFilterChange={handleDependentFilterChange}
          selectedIndepMeta={selectedIndepMeta}
          selectedDepMeta={selectedDepMeta}
          chartType={chartType}
          setChartType={setChartType}
          graphTitle={graphTitle}
          setGraphTitle={setGraphTitle}
          useWeightedData={useWeightedData}
          setUseWeightedData={setUseWeightedData}
          isFormComplete={Boolean(isFormComplete)}
          generateGraph={generateGraph}
          graphDataLength={graphData.length}
          exportToPDF={exportToPDF}
          isDataLoading={isLoading}
          dataError={dataError}
          isExporting={isExporting}
          exportError={exportError}
          comparisonStudyOptions={comparisonStudyOptions}
          selectedComparisonStudyNames={selectedComparisonStudyNames}
          setSelectedComparisonStudyNames={setSelectedComparisonStudyNames}
          canCompareBetweenYears={canCompareBetweenYears}
          isComparisonDataLoading={isComparisonDataLoading}
          comparisonDataError={comparisonDataError}
          yearComparisonError={effectiveYearComparisonError}
          onCompareBetweenYears={handleCompareBetweenYears}
          isYearComparisonActive={hasRenderableYearComparisonData}
          selectedYearLabel={baseYearLabel}
          activeComparisonYearLabels={activeComparisonYearLabels}
          outcomeResponseEntries={outcomeResponseEntries}
          numericOutcomeGuide={numericOutcomeGuide}
        />

        <div id="graph-container" className="flex flex-col gap-6">
          <ChartCanvas
            graphData={rendererGraphData}
            graphDataLength={rendererGraphData.length}
            graphTitle={graphTitle}
            chartType={chartType}
            barYAxisMetric={barYAxisMetric}
            setBarYAxisMetric={setBarYAxisMetric}
            hasComparison={hasComparisonCategory}
            dependentAnswerMapping={dependentAnswerMapping}
            selectedIndepMeta={rendererIndepMeta}
            selectedDepMeta={selectedDepMeta}
            isDataLoading={isLoading}
            dataError={dataError}
            graphError={graphError}
            yearComparisonSeries={chartType === "grouped_bar" ? yearComparisonSeries : []}
            yearComparisonEnabled={shouldOverlayYearComparison && chartType === "grouped_bar"}
            containerId="graph-container-primary"
            sourceLine={primarySourceLine}
          />

          {shouldShowComparisonBelow &&
            renderableComparisonResults.map((result, index) => (
              <ChartCanvas
                key={`${result.studyName}-${result.yearLabel}-${index}`}
                graphData={result.graphData}
                graphDataLength={result.graphData.length}
                graphTitle={`${graphTitle} (${result.yearLabel})`}
                chartType={chartType}
                barYAxisMetric={barYAxisMetric}
                setBarYAxisMetric={setBarYAxisMetric}
                hasComparison={hasComparisonCategory}
                dependentAnswerMapping={result.dependentAnswerMapping}
                selectedIndepMeta={selectedIndepMeta}
                selectedDepMeta={selectedDepMeta}
                isDataLoading={false}
                dataError={null}
                graphError={result.graphError || null}
                containerId={`graph-container-comparison-${index}`}
                sourceLine={formatSourceLine([result.studyName])}
              />
            ))}
        </div>

        <ShowStatsSection
          canShowStats={graphData.length > 0}
          showStats={showStats}
          onToggleStats={handleToggleStats}
        >
          {hasRenderableYearComparisonData ? (
            <div className="space-y-10">
              <section>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Statistics ({baseYearLabel})
                </h3>
                <ChartStatisticsPanel
                  nationalStats={nationalStats}
                  selectedDepMeta={selectedDepMeta}
                  dependentAnswerMapping={dependentAnswerMapping}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  indepStats={indepStats}
                  selectedIndepBucket={selectedIndepBucket}
                  setSelectedIndepBucket={setSelectedIndepBucket}
                  indepIsGrouped={indepIsGrouped}
                  detectNumericScale={detectNumericScale}
                  useWeightedData={useWeightedData}
                  hideByCategory
                />
              </section>

              {comparisonStatsByYear.map((comparisonStats) => (
                <section key={`stats-${comparisonStats.studyName}-${comparisonStats.yearLabel}`}>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Statistics ({comparisonStats.yearLabel})
                  </h3>
                  <ChartStatisticsPanel
                    nationalStats={comparisonStats.nationalStats}
                    selectedDepMeta={selectedDepMeta}
                    dependentAnswerMapping={comparisonStats.dependentAnswerMapping}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    indepStats={comparisonStats.indepStats}
                    selectedIndepBucket={selectedIndepBucket}
                    setSelectedIndepBucket={setSelectedIndepBucket}
                    indepIsGrouped={comparisonStats.indepIsGrouped}
                    detectNumericScale={detectNumericScale}
                    useWeightedData={useWeightedData}
                    hideByCategory
                  />
                </section>
              ))}

              {hasComparisonCategory &&
                comparisonStatsByYear.map((comparisonStats) => (
                  <ChartStatisticsComparisonByCategoryPanel
                    key={`by-cat-${comparisonStats.studyName}-${comparisonStats.yearLabel}`}
                    baseYearLabel={baseYearLabel}
                    comparisonYearLabel={comparisonStats.yearLabel}
                    selectedDepMeta={selectedDepMeta}
                    baseNationalStats={nationalStats}
                    comparisonNationalStats={comparisonStats.nationalStats}
                    baseDependentAnswerMapping={dependentAnswerMapping}
                    comparisonDependentAnswerMapping={comparisonStats.dependentAnswerMapping}
                    baseIndepStats={indepStats}
                    comparisonIndepStats={comparisonStats.indepStats}
                    selectedIndepBucket={selectedIndepBucket}
                    setSelectedIndepBucket={setSelectedIndepBucket}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    indepIsGrouped={indepIsGrouped}
                    useWeightedData={useWeightedData}
                  />
                ))}
            </div>
          ) : (
            <ChartStatisticsPanel
              nationalStats={nationalStats}
              selectedDepMeta={selectedDepMeta}
              dependentAnswerMapping={dependentAnswerMapping}
              sortBy={sortBy}
              setSortBy={setSortBy}
              indepStats={indepStats}
              selectedIndepBucket={selectedIndepBucket}
              setSelectedIndepBucket={setSelectedIndepBucket}
              indepIsGrouped={indepIsGrouped}
              detectNumericScale={detectNumericScale}
              useWeightedData={useWeightedData}
            />
          )}
        </ShowStatsSection>
      </div>
    </div>
  );
};

export default ChartForm;
